import { actaApi } from './actaApi';

export interface AdminPublication {
  id: string;
  entity_id: string;
  title?: string;
  summary?: string;
  published_at?: string | null;
  is_for_dom?: boolean;
  is_for_dou?: boolean;
  is_for_jgc?: boolean;
  categories?: Array<{ id: string; category: string }>;
  pdf_path?: string;
  entity?: {
    id: string;
    prefix: string;
    suffix: string;
    type: string;
    manager_name: string;
    editor_name: string;
  };
  uploaded_files_details?: Array<{
    name: string;
    url: string;
    order: number;
  }>;
}

export interface AdminPublicationListResponse {
  items: AdminPublication[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminPublicationCreatePayload {
  entity_id: string;
  is_for_dom?: boolean;
  is_for_dou?: boolean;
  is_for_jgc?: boolean;
  created_by: string;
  created_by_name: string;
  created_at?: string;
  updated_at?: string;
  published_at?: string;
  files: File[];
  // Adicionados para forçar edição e/ou ano romano (opcionais):
  forced_edition_number?: number;
  forced_year_roman_start_from?: number;
}

/**
 * Lista publicações Admin.
 * GET /publications?entity_id=...&page=...&limit=...
 */
export async function listAdminPublications(
  entityId?: string,
  page = 1,
  limit = 10
): Promise<AdminPublicationListResponse> {
  const params: Record<string, any> = { page, limit };
  if (entityId) params.entity_id = entityId;

  const response = await actaApi.get<AdminPublicationListResponse>('/publications', { params });
  return response.data;
}

/**
 * Detalha uma publicação Admin.
 * GET /publications/{publicationId}?entity_id=...
 */
export async function getAdminPublication(
  publicationId: string,
  entityId?: string
): Promise<AdminPublication> {
  const params: Record<string, any> = {};
  if (entityId) params.entity_id = entityId;

  const response = await actaApi.get<AdminPublication>(
    `/publications/${publicationId}`,
    { params }
  );
  return response.data;
}

/**
 * Cria uma nova publicação Admin (com Upload de arquivos).
 * POST /publications
 */
export async function createAdminPublication(
  payload: AdminPublicationCreatePayload
): Promise<AdminPublication> {
  const formData = new FormData();

  formData.append('entity_id', payload.entity_id);
  formData.append('created_by', payload.created_by);
  formData.append('created_by_name', payload.created_by_name);

  formData.append('is_for_dom', String(!!payload.is_for_dom));
  formData.append('is_for_dou', String(!!payload.is_for_dou));
  formData.append('is_for_jgc', String(!!payload.is_for_jgc));

  if (payload.created_at) formData.append('created_at', payload.created_at);
  if (payload.updated_at) formData.append('updated_at', payload.updated_at);
  if (payload.published_at) formData.append('published_at', payload.published_at);

  // Novos campos opcionais
  if (payload.forced_edition_number !== undefined) {
    formData.append('forced_edition_number', String(payload.forced_edition_number));
  }
  if (payload.forced_year_roman_start_from !== undefined) {
    formData.append('forced_year_roman_start_from', String(payload.forced_year_roman_start_from));
  }

  for (const file of payload.files) {
    formData.append('files', file);
  }

  const response = await actaApi.post<AdminPublication>(
    '/publications',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * Exclui uma publicação Admin.
 * DELETE /publications/{publicationId}?entity_id=...
 */
export async function deleteAdminPublication(
  publicationId: string,
  entityId?: string
): Promise<void> {
  const params: Record<string, any> = {};
  if (entityId) params.entity_id = entityId;

  await actaApi.delete(`/publications/${publicationId}`, { params });
}
